import { Link } from "lucide-react";
import CustomerCare from "../../../Components/CustomerCare";
import Payroll from "../../../Components/Payroll";
import grayremove from "../../../images/grayremove.svg";
import darkremove from "../../../images/darkremove.svg";
import remove from "../../../images/remove.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect } from "react";
import {
  faCheck,
  faExclamationTriangle,
  faBan,
  faClock,
  faArrowUp,
  faArrowRight,
  faArrowDown,
  faInfoCircle,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import {
  clearBetSlip,
  removeBet,
  setStake,
  validateBetSlip,
  clearErrors,
  clearNotification,
} from "../../../store/betslip-slice";
import { determineMarketOutcome } from "../../../utils/odds";
import { useCreateSportsBetMutation } from "../../../store/apis";
import {
  selectUser,
  setLoginModal,
  updateUser,
} from "../../../store/auth-slice";
import { toastService } from "../../notificationPage/ToastContainer";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../../utils";

const SportsBetSlip = ({ isDarkMode }) => {
  const [isChecked, setIsChecked] = useState(false);
  const [isToggled, setIsToggled] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { bets, totalOdds, totalStake, possibleWin, errors, notifications } =
    useSelector((state) => state.betSlip);

  const user = useSelector(selectUser);
  const [createBet, { isLoading }] = useCreateSportsBetMutation();

  // Clear errors when component mounts
  useEffect(() => {
    dispatch(clearErrors());
  }, [dispatch]);

  const goToBetHistory = () => {
    navigate("/bet-history");
  };

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const handleToggle = () => {
    setIsToggled(!isToggled);
  };

  // Enhanced remove bet handler that uses selectionId
  const handleRemoveBet = (bet) => {
    console.log("🗑️ Removing bet:", bet);
    dispatch(
      removeBet({
        selectionId: bet.selectionId,
        match: bet.match,
        odd: bet.odd,
        market: bet.market,
      })
    );
  };

  const handleStakeSelection = (stake) => {
    dispatch(setStake(stake));
  };

  const handleClearBetSlip = () => {
    dispatch(clearBetSlip());
  };

  // Handle notification dismissal
  const handleDismissNotification = (notificationId) => {
    dispatch(clearNotification({ notificationId }));
  };

  // Tax and calculation constants
  const EXCISE_TAX_RATE = 0.15;
  const WITHHOLDING_TAX_RATE = 0.2;

  const calculateTaxes = () => {
    const winAmount = possibleWin;
    const exciseTax = totalStake * EXCISE_TAX_RATE;
    const withholdingTax = winAmount * WITHHOLDING_TAX_RATE;
    const netPayout = winAmount - withholdingTax;

    return {
      exciseTax: exciseTax.toFixed(2),
      withholdingTax: withholdingTax.toFixed(2),
      netPayout: netPayout.toFixed(2),
    };
  };

  // Enhanced function to check if bet can be placed
  const canPlaceBet = () => {
    if (!user) return false;
    if (bets.length === 0) return false;
    if (totalStake < 1) return false;
    if (totalStake > user.balance) return false;

    const validBets = bets.filter((bet) => bet.isValid && bet.canBePlaced);
    if (validBets.length !== bets.length) return false;

    return true;
  };

  // Get invalid bets for display
  const getInvalidBets = () => {
    return bets.filter((bet) => !bet.isValid || !bet.canBePlaced);
  };

  // Enhanced trading status info
  const getTradingStatusInfo = (bet) => {
    const status = bet.tradingStatus || "Open";

    switch (status) {
      case "Closed":
        return {
          icon: faBan,
          text: "Market Closed",
          color: "text-red-500",
          bgColor: "bg-red-50 dark:bg-red-900/20",
        };
      case "Suspended":
        return {
          icon: faClock,
          text: "Market Suspended",
          color: "text-yellow-500",
          bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
        };
      case "Open":
      default:
        return null;
    }
  };

  // Enhanced odds change detection
  const hasOddsChanged = (bet) => {
    return (
      bet.oddsChanged ||
      (bet.odd.newValue && bet.odd.newValue !== bet.odd.currentValue)
    );
  };

  // Enhanced odds change info
  const getOddsChangeInfo = (bet) => {
    if (!hasOddsChanged(bet)) return null;

    const currentValue = parseFloat(bet.odd.currentValue || bet.odd.value);
    const newValue = parseFloat(bet.odd.newValue || bet.odd.currentValue);
    const isIncrease = newValue > currentValue;

    return {
      current: currentValue.toFixed(2),
      new: newValue.toFixed(2),
      isIncrease,
      color: isIncrease ? "text-green-500" : "text-red-500",
      arrow: isIncrease ? "↑" : "↓",
    };
  };

  // Enhanced status strip information
  const getStatusStripInfo = () => {
    const statusMessages = [];

    // Check for closed markets
    const closedBets = bets.filter((bet) => bet.tradingStatus === "Closed");
    if (closedBets.length > 0) {
      statusMessages.push({
        type: "error",
        icon: faBan,
        message: `${closedBets.length} market(s) closed - cannot be placed`,
        color: "text-red-600 dark:text-red-400",
        bgColor: "bg-red-100 dark:bg-red-900/30",
      });
    }

    // Check for suspended markets
    const suspendedBets = bets.filter(
      (bet) => bet.tradingStatus === "Suspended"
    );
    if (suspendedBets.length > 0) {
      statusMessages.push({
        type: "warning",
        icon: faClock,
        message: `${suspendedBets.length} market(s) suspended - betting may be unavailable`,
        color: "text-yellow-600 dark:text-yellow-400",
        bgColor: "bg-yellow-100 dark:bg-yellow-900/30",
      });
    }

    // Check for odds changes
    const changedOddsBets = bets.filter((bet) => hasOddsChanged(bet));
    if (changedOddsBets.length > 0) {
      const increaseCount = changedOddsBets.filter((bet) => {
        const current = parseFloat(bet.odd.currentValue || bet.odd.value);
        const newVal = parseFloat(bet.odd.newValue || bet.odd.currentValue);
        return newVal > current;
      }).length;

      const decreaseCount = changedOddsBets.length - increaseCount;

      let message = `${changedOddsBets.length} odds updated automatically`;
      if (increaseCount > 0 && decreaseCount > 0) {
        message += ` (${increaseCount} up, ${decreaseCount} down)`;
      } else if (increaseCount > 0) {
        message += ` (${increaseCount} increased)`;
      } else {
        message += ` (${decreaseCount} decreased)`;
      }

      statusMessages.push({
        type: "info",
        icon: faInfoCircle,
        message,
        color: "text-blue-600 dark:text-blue-400",
        bgColor: "bg-blue-100 dark:bg-blue-900/30",
      });
    }

    return statusMessages;
  };

  const handlePlaceBet = async () => {
    dispatch(validateBetSlip());

    if (!user) {
      dispatch(setLoginModal(true));
      toastService.error("Error! Please login to proceed!!");
      return;
    }

    if (totalStake < 1) {
      toastService.error("Error! Invalid amount!!");
      return;
    }

    if (totalStake > user.balance) {
      toastService.error("Error! Insufficient funds!!");
      return;
    }

    if (bets.length === 0) {
      toastService.error("Error! Empty bets");
      return;
    }

    const invalidBets = getInvalidBets();
    if (invalidBets.length > 0) {
      const invalidBetMessages = invalidBets
        .map(
          (bet) =>
            `${bet.odd.label} - Market ${bet.tradingStatus || "Unavailable"}`
        )
        .join(", ");
      toastService.error(`Cannot place bet: ${invalidBetMessages}`);
      return;
    }

    const validBets = bets.filter((bet) => bet.isValid && bet.canBePlaced);

    const betData = validBets.map((bet) => {
      const marketOutcome = determineMarketOutcome(
        bet.odd.label,
        bet.match.name || bet.match.gameName
      );
      const oddLabelName =
        marketOutcome.type === "Team" ? marketOutcome.team : marketOutcome.type;

      return {
        customer_id: user.id,
        match_id: bet.match.id,
        game_name: bet.match.name || bet.match.gameName,
        label: bet.odd.label,
        odd_label: oddLabelName,
        market_name: bet.market?.marketName || bet.match.market,
        event_date: bet.match.date,
        odd_value: bet.odd.currentValue || bet.odd.value,
        selection_id: bet.selectionId, // Use selectionId from enhanced bet
        stake_amount: totalStake,
        potential_payout: possibleWin,
        status: "pending",
        trading_status: bet.tradingStatus || "Open",
      };
    });

    try {
      console.log("BETDATA", betData);
      const response = await createBet(betData).unwrap();
      toastService.success(`${response.betSlipId} ${response.message}`);
      dispatch(updateUser(response.user));

      if (!isToggled) {
        dispatch(clearBetSlip());
      }
    } catch (error) {
      console.error("Error placing bet:", error);
      toastService.error(
        "Error placing bet: " + (error.message || "Unknown error")
      );
    }
  };

  const taxes = calculateTaxes();
  const hasBets = bets && bets.length > 0;
  const invalidBets = getInvalidBets();
  const canPlace = canPlaceBet();
  const statusStripInfo = getStatusStripInfo();

  return (
    <div className="col-span-0 hidden s15:block s15:col-span-3">
      <div className="odd-conected rounded-[7px] bg-white dark:bg-bluedarkmodecolor shadow-dashboardshadow w-full">
        <div className="flex items-center rounded-[7px_7px_0px_0px] bg-darkblue dark:bg-bluedarkmode px-[15px]">
          <span className="border-solid border-r-[1px] border-r-doubleextralightwhite w-[50%] flex items-center gap-[7px] py-[10px] md:py-2 s18:py-[10px] text-white text-base md:text-sm s18:text-[18px] font-medium leading-[100%]">
            <span className="flex w-8 md:w-6 s18:w-8 h-8 md:h-6 s18:h-8 justify-center items-center bg-yellow rounded-[50%] text-black text-[19px] md:text-base s18:text-[19px] font-medium !leading-none">
              {bets.length}
            </span>{" "}
            Betslip
          </span>
          <span
            onClick={goToBetHistory}
            className="cursor-pointer w-[50%] flex justify-center items-center py-[10px] md:py-2 s18:py-[10px] text-white text-base md:text-sm s18:text-[18px] font-medium leading-[100%]"
          >
            My bets
          </span>
        </div>

        {/* Display errors if any */}
        {errors.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-3 m-3">
            <div className="flex items-center">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-red-500 mr-2"
              />
              <div>
                <p className="text-red-700 dark:text-red-300 text-sm font-medium">
                  Betting Errors:
                </p>
                <ul className="text-red-600 dark:text-red-400 text-xs mt-1">
                  {errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Display notifications */}
        {notifications.length > 0 && (
          <div className="p-3 m-3 space-y-2">
            {notifications.slice(0, 3).map((notification) => (
              <div
                key={notification.id}
                className={`p-2 rounded-md text-xs flex items-center justify-between ${
                  notification.type === "odds_change"
                    ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                    : notification.type === "status_change"
                    ? "bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300"
                    : "bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-300"
                }`}
              >
                <span>{notification.message}</span>
                <button
                  onClick={() => handleDismissNotification(notification.id)}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </div>
            ))}
          </div>
        )}

        {hasBets ? (
          <>
            <span
              onClick={handleClearBetSlip}
              className="cursor-pointer border-solid border-b-[2px] border-b-extrathinblack dark:border-b-lightwhiteborder flex p-3 md:p-2 md:px-3 s18:p-3 bg-extralightblack dark:bg-[#2F4058] items-center gap-[12px] text-[#EF4444] text-sm font-medium leading-[128%]"
            >
              <img src={remove} alt="remove" />
              Remove all
            </span>

            {bets.map((bet, index) => {
              const marketOutcome = determineMarketOutcome(
                bet.odd.label,
                bet.match.name || bet.match.gameName
              );
              const tradingStatusInfo = getTradingStatusInfo(bet);
              const oddsChangeInfo = getOddsChangeInfo(bet);

              // Get individual bet status strips
              const getBetStatusStrips = (bet) => {
                const strips = [];

                // Trading Status Strip
                if (tradingStatusInfo) {
                  strips.push({
                    type: "trading",
                    icon: tradingStatusInfo.icon,
                    message: tradingStatusInfo.text,
                    bgColor:
                      tradingStatusInfo.text === "Market Closed"
                        ? "bg-red-100 dark:bg-red-900/30 border-l-4 border-red-500"
                        : "bg-yellow-100 dark:bg-yellow-900/30 border-l-4 border-yellow-500",
                    textColor: tradingStatusInfo.color,
                  });
                }

                // Odds Change Strip
                if (oddsChangeInfo) {
                  strips.push({
                    type: "odds_change",
                    icon: oddsChangeInfo.isIncrease ? faArrowUp : faArrowDown,
                    message: `Odds automatically updated from ${oddsChangeInfo.current} to ${oddsChangeInfo.new}`,
                    bgColor: oddsChangeInfo.isIncrease
                      ? "bg-green-100 dark:bg-green-900/30 border-l-4 border-green-500"
                      : "bg-orange-100 dark:bg-orange-900/30 border-l-4 border-orange-500",
                    textColor: oddsChangeInfo.color,
                  });
                }

                return strips;
              };

              const betStatusStrips = getBetStatusStrips(bet);

              return (
                <div key={bet.id || index}>
                  <div className="flex justify-between p-[10px_13px_8px] s18:p-[15px_15px_10px] border-solid border-b-[1px] border-b-extrathinblack dark:border-b-lightwhiteborder">
                    <div className="flex gap-[12px] items-start">
                      <button
                        onClick={() => handleRemoveBet(bet)}
                        className="cursor-pointer mt-[3px]"
                      >
                        <img
                          src={remove}
                          alt="remove"
                          className="cursor-pointer mt-[3px]"
                        />
                      </button>
                      <div className="flex flex-col gap-1 s18:gap-[5px] cursor-pointer">
                        <p className="text-darkblue dark:text-white text-base md:text-sm s18:text-base font-medium leading-tight">
                          {marketOutcome.type === "Team"
                            ? marketOutcome.team
                            : marketOutcome.type}
                        </p>
                        <p className="text-[#757575] dark:text-white text-sm md:text-xs s18:text-sm font-normal leading-tight">
                          {bet.match.name || bet.match.gameName}
                        </p>
                        <p className="text-[#757575] dark:text-white text-sm md:text-xs s18:text-sm font-normal leading-tight">
                          {bet.market?.marketName || bet.match.market}
                        </p>
                        <p className="text-[#757575] dark:text-white text-sm md:text-xs s18:text-sm font-normal leading-tight">
                          {formatDate(bet.match.date) || ""}
                        </p>
                      </div>
                    </div>

                    {/* Enhanced odds display with change indication */}
                    <span
                      className={`h-full rounded-[6px] flex justify-center items-center p-[5px_9px] text-base font-semibold leading-[125%] bg-lightyellow dark:bg-yellow dark:text-darkblue text-darkblue"
                        }`}
                    >
                      {/* <span
                        className={`h-full rounded-[6px] flex justify-center items-center p-[5px_9px] text-base font-semibold leading-[125%] ${
                          hasOddsChanged(bet)
                            ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                            : "bg-lightyellow dark:bg-yellow dark:text-darkblue text-darkblue"
                        }`}
                      > */}
                      {bet.odd.newValue ||
                        bet.odd.currentValue ||
                        bet.odd.value}
                    </span>

                    {/* Show previous odds if changed */}
                    {/* {hasOddsChanged(bet) && (
                        <span className="text-xs text-gray-500 line-through mt-1">
                          {bet.odd.currentValue || bet.odd.value}
                        </span>
                      )} */}
                  </div>

                  {/* Individual Status Strips for this bet */}
                  {betStatusStrips.map((strip, stripIndex) => (
                    <div
                      key={`${bet.id || index}-${stripIndex}`}
                      className={`${strip.bgColor} p-2 mx-3 mb-1`}
                    >
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                          icon={strip.icon}
                          className={`${strip.textColor} text-sm`}
                        />
                        <span
                          className={`${strip.textColor} text-xs font-medium flex-1`}
                        >
                          {strip.message}
                        </span>
                      </div>
                    </div>
                  ))}

                  {/* Add border after the last bet if it's not the last item */}
                  {index < bets.length - 1 && (
                    <div className="border-b-[1px] border-b-extrathinblack dark:border-b-lightwhiteborder mx-3"></div>
                  )}
                </div>
              );
            })}

            {/* Final border after all bets */}
            <div className="border-b-[2px] border-b-extrathinblack dark:border-b-lightwhiteborder"></div>

            <div className="p-[15px_15px_0px] md:p-[12px_12px_0px] s18:p-[15px_15px_0px]">
              <span className="text-[#EF4444] text-base md:text-sm s18:text-base font-medium leading-[112%]">
                Stake
              </span>
              <div className="flex gap-[5px] mt-[5px] md:mt-3 s18:mt-[5px]">
                {[20, 50, 100, 500].map((stakeAmount) => (
                  <span
                    key={stakeAmount}
                    onClick={() => handleStakeSelection(stakeAmount)}
                    className="cursor-pointer p-[10px_14px] xl:p-[8px_12px] s18:p-[12px_14px] s18:w-[58px] s18:h-[43px] flex justify-center items-center rounded-[6px] bg-extralightblack border border-solid border-lightblack text-black dark:text-yellow text-base md:text-sm s18:text-base font-medium !leading-none s18:leading-[112%] hover:bg-yellow hover:border-bordercolor transition-all duration-[0.3s] dark:bg-darktitleColor dark:border-doubleextralightwhite dark:text-white dark:hover:bg-yellow dark:hover:text-darkblue"
                  >
                    {stakeAmount}/-
                  </span>
                ))}
                <span className="flex justify-end p-[10px_14px] xl:p-[8px_12px] s18:p-[12px_14px] items-center w-full s18:h-[43px] rounded-[6px] border-solid border-bordercolor bg-darkblue text-[18px] text-base s18:text-[18px] text-white font-semibold !leading-none s18:leading-[120%] dark:text-yellow dark:border dark:border-solid dark:bg-bluedarkmode dark:border-doubleextralightwhite">
                  {totalStake}
                </span>
              </div>

              <div className="p-[15px_0px] md:p-[12px_0] s18:p-[15px_0px] flex flex-col gap-[10px] md:gap-2 s18:gap-[10px]">
                <p className="flex justify-between items-center text-darkblue dark:text-white text-base md:text-sm s18:text-base font-normal !leading-none s18:!leading-[112%]">
                  Total Odds{" "}
                  <span className="text-darkblue dark:text-white text-base md:text-sm s18:text-base font-semibold !leading-none s18:!leading-[125%]">
                    {totalOdds.toFixed(2)}
                  </span>
                </p>
                <p className="flex justify-between items-center text-darkblue dark:text-white text-base md:text-sm s18:text-base font-normal !leading-none s18:!leading-[112%]">
                  Possible Win{" "}
                  <span className="flex gap-[5px] text-darkblue dark:text-white text-base md:text-sm s18:text-base font-semibold !leading-none s18:!leading-[125%]">
                    <span className="text-base md:text-sm s18:text-base text-thinblack dark:text-extralightwhite font-semibold !leading-none s18:!leading-[125%]">
                      KShs.
                    </span>{" "}
                    {possibleWin.toFixed(2)}
                  </span>
                </p>
                <p className="flex justify-between items-center text-darkblue dark:text-white text-base md:text-sm s18:text-base font-normal !leading-none s18:!leading-[112%]">
                  15% Excise Tax{" "}
                  <span className="flex gap-[5px] text-darkblue dark:text-white text-base font-semibold !leading-none s18:!leading-[125%]">
                    <span className="text-base text-thinblack font-semibold !leading-none s18:!leading-[125%] dark:text-extralightwhite">
                      KShs.
                    </span>{" "}
                    {taxes.exciseTax}
                  </span>
                </p>
              </div>

              <div className="rounded-[0px_0px_7px_7px] bg-bglightblue dark:bg-bluedarkmode p-[15px_15px_20px] md:p-[12px_12px_15px] s18:p-[15px_15px_20px] flex flex-col gap-[10px] md:gap-2 s18:gap-[10px] -mx-[15px] md:-mx-[12px] s18:-mx-[15px]">
                <p className="flex justify-between items-center text-darkblue dark:text-white text-base md:text-sm s18:text-base font-normal leading-[112%] md:leading-none s18:leading-[112%]">
                  Win{" "}
                  <span className="text-darkblue dark:text-white text-base md:text-sm s18:text-base font-semibold leading-[125%]">
                    <span className="text-base md:text-sm s18:text-base text-thinblack dark:text-extralightwhite font-semibold leading-[125%]">
                      KShs.
                    </span>{" "}
                    {possibleWin.toFixed(2)}
                  </span>
                </p>
                <p className="flex justify-between items-center text-darkblue dark:text-white text-base md:text-sm s18:text-base font-normal leading-[112%] md:leading-none s18:leading-[112%]">
                  20% Withholding Tax{" "}
                  <span className="flex gap-[5px] text-darkblue dark:text-white text-base md:text-sm s18:text-base font-semibold leading-[125%]">
                    <span className="text-base md:text-sm s18:text-base text-thinblack dark:text-extralightwhite font-semibold leading-[125%]">
                      KShs.
                    </span>{" "}
                    {taxes.withholdingTax}
                  </span>
                </p>
                <p className="flex justify-between items-center text-darkblue dark:text-white text-base md:text-sm s18:text-base font-normal leading-[112%] md:leading-none s18:leading-[112%]">
                  Possible Payout{" "}
                  <span className="flex gap-[5px] text-darkblue dark:text-white text-base md:text-sm s18:text-base font-semibold leading-[125%]">
                    <span className="text-base md:text-sm s18:text-base text-thinblack font-semibold leading-[125%] dark:text-extralightwhite">
                      KShs.
                    </span>{" "}
                    {taxes.netPayout}
                  </span>
                </p>

                <div className="flex justify-between items-center mt-10 md:mt-7 s18:mt-10">
                  <label className="flex items-center text-sm font-normal leading-[128%] text-thinblack dark:text-extralightwhite select-none">
                    <input
                      type="checkbox"
                      className="hidden peer"
                      checked={isChecked}
                      onChange={handleCheckboxChange}
                    />
                    <span className="mr-[9px] w-[18px] h-[18px] rounded-[2px] border-[1px] bg-transparent border-solid border-[#A3A3A3] peer-checked:bg-green-600 peer-checked:border-green-600 flex justify-center items-center">
                      {isChecked ? (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="text-white ml-[1px] font-bold opacity-100"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="text-gray-400 opacity-0"
                        />
                      )}
                    </span>
                    Auto-update odds
                  </label>

                  <div className="flex items-center gap-[9px]">
                    <div
                      onClick={handleToggle}
                      className={`relative w-[30px] h-[20px] flex items-center rounded-[11px] cursor-pointer ${
                        isToggled ? "bg-[#11A306]" : "bg-[#A3A3A3]"
                      }`}
                    >
                      <div
                        className={`w-[14px] h-[14px] bg-white rounded-[11px] transform transition-transform ${
                          isToggled ? "translate-x-3" : "translate-x-1"
                        }`}
                      ></div>
                    </div>
                    <span className="text-sm text-thinblack font-medium leading-[128%] dark:text-extralightwhite">
                      Keep betslip
                    </span>
                  </div>
                </div>

                <div className="flex gap-[9px] mt-[15px] md:mt-3 s18:mt-[15px]">
                  <button
                    className="inline-flex bg-darkblue text-yellow !text-yellow border-[2px] border-solid border-darkblue dark:border-white 
    hover:bg-darkblue/90 hover:!text-yellow 
    dark:hover:bg-white dark:hover:text-darkblue
    transition-all duration-[0.3s] dark:bg-white rounded-[7px] dark:text-darkblue 
    p-[16px_17px] md:p-[8px_14px] s18:p-[16px_17px] 
    text-lg md:text-sm s18:text-lg font-medium leading-[111%]"
                  >
                    Share
                  </button>

                  <button
                    disabled={isLoading || !canPlace}
                    onClick={handlePlaceBet}
                    className={`rounded-[7px] cursor-pointer flex justify-center items-center w-full p-[16px_17px] md:p-[8px_14px] s18:p-[15px] text-lg md:text-sm s18:text-lg font-medium leading-[111%] transition-all duration-300 ${
                      canPlace && !isLoading
                        ? "bg-yellow hover:bg-yellow/70 text-darkblue"
                        : "bg-gray-400 dark:bg-gray-600 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <svg
                          className="animate-spin h-5 w-5 mr-2 text-current"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l3 3-3 3v-4a8 8 0 01-8-8z"
                          ></path>
                        </svg>
                        Placing Bet...
                      </div>
                    ) : !canPlace ? (
                      "Cannot Place Bet"
                    ) : (
                      <>
                        Place Bet KShs. <span>{totalStake}</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Enhanced "why bet cannot be placed" messages */}
                {!canPlace && !isLoading && (
                  <div className="text-xs text-red-500 dark:text-red-400 mt-2">
                    {!user && "Please log in to place bets"}
                    {user && bets.length === 0 && "No bets selected"}
                    {user && totalStake < 1 && "Minimum stake is 1 KShs"}
                    {user &&
                      totalStake > user.balance &&
                      "Insufficient balance"}
                    {user &&
                      invalidBets.length > 0 &&
                      `${invalidBets.length} bet(s) unavailable (${invalidBets
                        .map((b) => b.tradingStatus)
                        .join(", ")})`}
                  </div>
                )}

                {/* Enhanced status summary */}
                {bets.length > 0 && (
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-2 space-y-1">
                    <div className="flex justify-between">
                      <span>Valid bets:</span>
                      <span className="text-green-600 dark:text-green-400">
                        {bets.filter((b) => b.isValid && b.canBePlaced).length}/
                        {bets.length}
                      </span>
                    </div>
                    {notifications.length > 0 && (
                      <div className="flex justify-between">
                        <span>Notifications:</span>
                        <span className="text-blue-600 dark:text-blue-400">
                          {notifications.length}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-4 border-b border-extrathinblack dark:border-lightwhiteborder">
            <div className="w-16 h-16 mb-4 flex items-center justify-center rounded-full bg-gray-100 dark:bg-[#2F4058]">
              <svg
                className="w-8 h-8 text-gray-400 dark:text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                ></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium text-darkblue dark:text-white mb-2">
              No Selection
            </h3>
            <p className="text-center text-gray-500 dark:text-gray-400 text-sm mb-4">
              Please select odds from the available markets to add to your
              betslip.
            </p>
            <button
              onClick={goToBetHistory}
              className="px-4 py-2 bg-darkblue text-white dark:bg-yellow dark:text-darkblue rounded-md hover:bg-opacity-90 dark:hover:bg-opacity-90 transition-all"
            >
              View Bet History
            </button>
          </div>
        )}
      </div>

      <div>
        <Payroll />
      </div>
      <div>
        <CustomerCare />
      </div>
    </div>
  );
};

export default SportsBetSlip;
